// frontend/src/CoachView.js
import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "./api";
import CoachLineups from "./CoachLineups";
import LeanLineups from "./LeanLineups";
import RosterList from "./RosterList";
import RosterAttendance from "./RosterAttendance";
import ConfigSettings from "./ConfigSettings";
import CoachAttendance from "./CoachAttendance";
import EquipmentTasks from "./EquipmentTasks";
import WorkoutTracker from "./WorkoutTracker";
import OyoWorkoutModal from "./OyoWorkoutModal";
import PracticeCalendar from "./PracticeCalendar";
import CoachHomePage from "./CoachHomePage";
import AvailabilityModal from "./AvailabilityModal";
import LaunchAssignmentGrid from "./LaunchAssignmentGrid";




function toLocalYmd(value) {
  if (!value) return "";
  let dateObj;
  if (value instanceof Date) {
    dateObj = new Date(value.getTime());
  } else if (typeof value === "string") {
    dateObj = value.includes("T") ? new Date(value) : new Date(`${value}T00:00:00`);
  } else {
    dateObj = new Date(value);
  }
  if (Number.isNaN(dateObj.getTime())) return "";
  dateObj.setMinutes(dateObj.getMinutes() - dateObj.getTimezoneOffset());
  return dateObj.toISOString().slice(0, 10);
}

function todayLocalYmd() {
  return toLocalYmd(new Date());
}

/** ---- Lineups ---- */
function LineupsContent({ squads: _squads, coach, focusRequest = null }) {
  const [date, setDate] = useState(() => todayLocalYmd());
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focusPracticeId, setFocusPracticeId] = useState(null);
  const lastProcessedFocusRef = useRef(null);
  const [leanView, setLeanView] = useState(() => localStorage.getItem("lineupsLeanView") === "1");

  useEffect(() => {
    localStorage.setItem("lineupsLeanView", leanView ? "1" : "0");
  }, [leanView]);

  const loadDay = useCallback(async (targetDate) => {
    try {
      setLoading(true);
      setError("");
      const data = await apiFetch(`/dashboard/day?date=${targetDate}`);
      const canonical = data?.date || targetDate;
      const normalized = (Array.isArray(data?.attendance) ? data.attendance : []).map((ath) => {
        const squadName = ath.squadName || ath.squad || null;
        return {
          ...ath,
          squadId: ath.squadId ?? null,
          squadName,
          squad: squadName,
        };
      });
      setRoster(normalized);
      setDate(toLocalYmd(canonical));
    } catch (e) {
      setError(e?.message || "Failed to load lineup data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDay(date);
  }, [date, loadDay]);

  useEffect(() => {
    if (focusRequest?.date && focusRequest?.ts && lastProcessedFocusRef.current !== focusRequest.ts) {
      lastProcessedFocusRef.current = focusRequest.ts;
      const normalizedDate = toLocalYmd(focusRequest.date);
      if (normalizedDate) {
        setDate(normalizedDate);
      }
      setFocusPracticeId(focusRequest.practiceId ?? null);
    }
  }, [focusRequest?.date, focusRequest?.ts, focusRequest?.practiceId]);

  const shiftDay = (delta) => {
    const base = new Date(`${date}T00:00:00`);
    base.setDate(base.getDate() + delta);
    setDate(toLocalYmd(base));
  };

  const goToday = () => {
    setDate(todayLocalYmd());
  };

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="card" style={{ padding: 12 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
          <h2 className="h2" style={{ margin: 0 }}>
            Lineups • {new Date(`${date}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
          </h2>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn ghost small" onClick={() => shiftDay(-1)}>Prev</button>
            <button className="btn ghost small" onClick={goToday}>Today</button>
            <button className="btn ghost small" onClick={() => shiftDay(1)}>Next</button>
            <label className="row" style={{ gap: 6, fontSize: 13, marginLeft: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={leanView}
                onChange={(e) => setLeanView(e.target.checked)}
              />
              Lean view
            </label>
          </div>
        </div>
        {error && <p className="help" style={{ color: "red", marginTop: 8 }}>{error}</p>}
      </div>

      {leanView ? (
        <LeanLineups coach={coach} date={date} />
      ) : loading ? (
        <div className="card" style={{ padding: 12 }}>Loading lineups…</div>
      ) : (
        <CoachLineups date={date} practiceId={focusPracticeId} roster={roster} coachTeam={coach?.team || ""} coach={coach} />
      )}
    </div>
  );
}


/** ---- Profile ---- */
function ProfileContent({ coach }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <h2 className="h2" style={{ marginTop: 0 }}>Profile</h2>
      <div className="stack" style={{ gap: 6 }}>
        <div><strong>Name:</strong> {coach?.name || "Coach"}</div>
        <div><strong>Email:</strong> {coach?.email || "—"}</div>
        <div><strong>Role:</strong> {coach?.role || "Coach"}</div>
         <div><strong>Team:</strong> {coach?.team || "-"}</div>
      </div>
    </div>
  );
}

/** ---- Main switcher (your requested pattern) ---- */
export default function CoachView({
  activeTab,
  coach,
  navSignal,
  onNavigateToCalendar,
  initialOyoWorkoutId,
  onConsumeOyoWorkoutLink,
}) {
  const [squads, setSquads] = useState([]);
  const [roster, setRoster] = useState([]);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterError, setRosterError] = useState("");
  const extractRosterList = (data) => {
  if (Array.isArray(data?.roster)) return data.roster;
  if (Array.isArray(data)) return data;
  return [];
  };
  const canEditPractices =
    coach?.permissions?.includes?.("practices:edit") ??
    coach?.can_edit_practices ??
    false;
  const canViewWorkouts =
    coach?.permissions?.includes?.("workouts:view") ??
    coach?.can_view_workouts ??
    true;
  const [teamColors, setTeamColors] = useState({});
  const [oyoWorkoutModalId, setOyoWorkoutModalId] = useState(null);
  const [workoutFocusRequest, setWorkoutFocusRequest] = useState(null);
  const [lineupFocusRequest, setLineupFocusRequest] = useState(null);
  const [maintenanceFocusRequest, setMaintenanceFocusRequest] = useState(null);
  const [calendarFocusRequest, setCalendarFocusRequest] = useState(null);

  useEffect(() => {
    if (initialOyoWorkoutId) {
      setOyoWorkoutModalId(initialOyoWorkoutId);
      onConsumeOyoWorkoutLink?.();
    }
  }, [initialOyoWorkoutId, onConsumeOyoWorkoutLink]);


  // State for maintenance task modals
  const [editingMaintenanceTask, setEditingMaintenanceTask] = useState(null);
  const [maintenanceEditForm, setMaintenanceEditForm] = useState({
    status: '',
    repairNotes: '',
    assignedTo: ''
  });
  const [allMaintenanceTasks, setAllMaintenanceTasks] = useState([]);
  const [coachList, setCoachList] = useState([]);

  // State for availability modal
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [availabilityPracticeId, setAvailabilityPracticeId] = useState(null);
  const [homeRefreshKey, setHomeRefreshKey] = useState(0);
  useEffect(() => {
    if (navSignal && navSignal.tab === "workouts") {
      setWorkoutFocusRequest({
        ...(navSignal.payload || {}),
        ts: navSignal.ts || Date.now(),
      });
    }
  }, [navSignal]);
  useEffect(() => {
    if (navSignal && navSignal.tab === "lineups") {
      setLineupFocusRequest({
        ...(navSignal.payload || {}),
        ts: navSignal.ts || Date.now(),
      });
    }
  }, [navSignal]);
  useEffect(() => {
    if (navSignal && navSignal.tab === "maintenance") {
      setMaintenanceFocusRequest({
        ...(navSignal.payload || {}),
        ts: navSignal.ts || Date.now(),
      });
    }
  }, [navSignal]);
  useEffect(() => {
    if (navSignal && navSignal.tab === "calendar") {
      setCalendarFocusRequest({
        ...(navSignal.payload || {}),
        ts: navSignal.ts || Date.now(),
      });
    }
  }, [navSignal]);

  // Clear maintenance focus request when leaving the maintenance tab so the
  // "new-task" action doesn't auto-fire again on the next visit.
  useEffect(() => {
    if (activeTab !== "maintenance") {
      setMaintenanceFocusRequest(null);
    }
  }, [activeTab]);
  const renderWithOyoModal = (content) => (
    <>
      {content}
      {oyoWorkoutModalId && (
        <OyoWorkoutModal workoutId={oyoWorkoutModalId} onClose={() => setOyoWorkoutModalId(null)} />
      )}

      {/* Availability Modal */}
      <AvailabilityModal
        user={coach}
        userType="coach"
        isOpen={showAvailabilityModal}
        practiceId={availabilityPracticeId}
        onClose={() => {
          setShowAvailabilityModal(false);
          setAvailabilityPracticeId(null);
          setHomeRefreshKey((k) => k + 1);
        }}
        onNavigateToCalendar={onNavigateToCalendar}
      />

      {/* Maintenance Task Edit Modal */}
      {editingMaintenanceTask && (
        <div className="modalOverlay" onClick={() => setEditingMaintenanceTask(null)}>
          <div className="modalCard roster-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close"
              aria-label="Close maintenance modal"
              onClick={() => setEditingMaintenanceTask(null)}
            >
              &times;
            </button>
            <div className="roster-modal-header">
              <h3 className="h2 roster-modal-title">
                {(coach?.permissions?.includes?.("maintenance:update") || coach?.can_update_maintenance) ? "Update Maintenance Task" : "Maintenance Task Details"}
              </h3>
            </div>
            <div className="stack roster-modal-body">
              {(() => {
                const task = allMaintenanceTasks.find(t => t.id === editingMaintenanceTask);
                if (!task) return <p className="help">Task not found</p>;

                const formatDate = (dateString) => {
                  const date = new Date(dateString);
                  return date.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  });
                };

                const friendlyStatus = (task.status || maintenanceEditForm.status || "submitted")
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase());

                const canUpdateMaintenance = !!(coach?.permissions?.includes?.("maintenance:update") || coach?.can_update_maintenance);

                const statusColors = {
                  submitted:   { bg: 'var(--warn-bg)',    border: 'var(--warn-border)',    color: 'var(--warn)'    },
                  in_progress: { bg: 'rgba(59,130,246,0.12)', border: 'rgba(59,130,246,0.3)', color: '#93c5fd' },
                  completed:   { bg: 'var(--success-bg)', border: 'var(--success-border)', color: 'var(--success)' },
                };
                const sc = statusColors[task.status] || statusColors.submitted;

                return (
                  <>
                    {/* Equipment header */}
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-strong)' }}>{task.equipment_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginTop: 2 }}>{task.equipment_type}</div>
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap', background: sc.bg, border: `1px solid ${sc.border}`, color: sc.color }}>
                        {friendlyStatus}
                      </span>
                    </div>

                    {/* Description */}
                    <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 6 }}>Issue</div>
                      <p style={{ margin: 0, fontSize: 14, color: 'var(--text-strong)', lineHeight: 1.5 }}>{task.description}</p>
                    </div>

                    {/* Meta */}
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      Submitted by <strong style={{ color: 'var(--text)' }}>{task.submitted_by_name}</strong> · {formatDate(task.created_at)}
                    </div>

                    {/* Status + Assigned row */}
                    {canUpdateMaintenance ? (
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                        <label className="stack" style={{ gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)' }}>Status</span>
                          <select
                            className="input"
                            value={maintenanceEditForm.status}
                            onChange={(e) => setMaintenanceEditForm({ ...maintenanceEditForm, status: e.target.value })}
                          >
                            <option value="submitted">Submitted</option>
                            <option value="in_progress">In Progress</option>
                            <option value="completed">Completed</option>
                          </select>
                        </label>
                        <label className="stack" style={{ gap: 6 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)' }}>Assigned To</span>
                          <select
                            className="input"
                            value={maintenanceEditForm.assignedTo}
                            onChange={(e) => setMaintenanceEditForm({ ...maintenanceEditForm, assignedTo: e.target.value })}
                          >
                            <option value="">Unassigned</option>
                            {coachList.filter(u => u.role === 'coach').map(u => (
                              <option key={u.id} value={String(u.id)}>{u.name}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    ) : task.assigned_to_name ? (
                      <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                        Assigned to <strong style={{ color: 'var(--text)' }}>{task.assigned_to_name}</strong>
                      </div>
                    ) : null}

                    {/* Repair notes field */}
                    {canUpdateMaintenance ? (
                      <label className="stack" style={{ width: '100%', gap: 6 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)' }}>Repair Notes</span>
                        <textarea
                          className="input"
                          style={{ minHeight: 80, resize: 'vertical', fontFamily: 'inherit' }}
                          placeholder="Add repair notes..."
                          value={maintenanceEditForm.repairNotes}
                          onChange={(e) => setMaintenanceEditForm({ ...maintenanceEditForm, repairNotes: e.target.value })}
                        />
                      </label>
                    ) : task.repair_notes ? (
                      <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px', color: 'var(--muted)', marginBottom: 6 }}>Repair Notes</div>
                        <p style={{ margin: 0, fontSize: 14, color: 'var(--text-strong)', lineHeight: 1.5 }}>{task.repair_notes}</p>
                      </div>
                    ) : null}

                    {/* Actions */}
                    {canUpdateMaintenance && (
                      <div className="row" style={{ gap: 8, marginTop: 4, justifyContent: 'space-between' }}>
                        <button
                          className="btn danger small"
                          onClick={async () => {
                            if (!window.confirm('Delete this task?')) return;
                            try {
                              await apiFetch(`/equipment-tasks/${task.id}`, { method: 'DELETE' });
                              const tasks = await apiFetch('/equipment-tasks');
                              setAllMaintenanceTasks(tasks);
                              setEditingMaintenanceTask(null);
                            } catch (e) {
                              alert(e?.message || 'Failed to delete task');
                            }
                          }}
                        >
                          Delete
                        </button>
                        <button
                          className="btn small"
                          onClick={async () => {
                            try {
                              await apiFetch(`/equipment-tasks/${editingMaintenanceTask}`, {
                                method: 'PATCH',
                                body: JSON.stringify({
                                  status: maintenanceEditForm.status,
                                  repairNotes: maintenanceEditForm.repairNotes,
                                  assignedTo: maintenanceEditForm.assignedTo || null
                                })
                              });
                              const tasks = await apiFetch('/equipment-tasks');
                              setAllMaintenanceTasks(tasks);
                              setEditingMaintenanceTask(null);
                            } catch (e) {
                              alert(e?.message || 'Failed to update task');
                            }
                          }}
                        >
                          Save Changes
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </>
  );
  // 1. Single function to load roster
  const loadRoster = useCallback(async () => {
  try {
    setRosterError("");
    setRosterLoading(true);
    const data = await apiFetch("/roster");
    const list = extractRosterList(data);
    setRoster(list);
  } catch (e) {
    setRoster([]);
    setRosterError(e?.message || "Failed to load roster");
  } finally {
    setRosterLoading(false);
  }
}, []);
  const handleRosterUpdated = async () => {
  await loadRoster();
};

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch("/squads");
        if (!cancelled) {
          setSquads(Array.isArray(data) ? data : []);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("load squads error", e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch("/teams");
        if (cancelled) return;
        const map = {};
        (Array.isArray(data) ? data : []).forEach((team) => {
          if (!team?.name) return;
          map[String(team.name).trim().toLowerCase()] = team.color || null;
        });
        setTeamColors(map);
      } catch (e) {
        if (!cancelled) {
          console.error("load teams error", e);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeTab]);

useEffect(() => {
  if (activeTab === "roster" || activeTab === "attendance") {
    loadRoster();
  }
}, [activeTab, loadRoster]);

  if (activeTab === "home") {
    return renderWithOyoModal(
      <CoachHomePage
        coach={coach}
        refreshKey={homeRefreshKey}
        onAvailabilityClick={(practiceId) => {
          setAvailabilityPracticeId(practiceId || null);
          setShowAvailabilityModal(true);
        }}
        onMaintenanceRequestClick={() => {
          // Navigate to maintenance tab and request the new task modal
          window.dispatchEvent(
            new CustomEvent("coach:navigate", {
              detail: { tab: "maintenance", payload: { action: "new-task" } }
            })
          );
        }}
        onLineupClick={(date, practiceId) => {
          window.dispatchEvent(
            new CustomEvent("coach:navigate", {
              detail: { tab: "lineups", payload: { date, practiceId } }
            })
          );
        }}
        onPracticeClick={(practiceId, practiceDate) => {
          // Navigate to the calendar tab and request the same editable practice
          // modal that clicking a practice in the calendar opens.
          window.dispatchEvent(
            new CustomEvent("coach:navigate", {
              detail: { tab: "calendar", payload: { practiceId, date: practiceDate } }
            })
          );
        }}
        onMaintenanceClick={async (taskId) => {
          try {
            const [tasks, coaches] = await Promise.all([
              apiFetch('/equipment-tasks'),
              apiFetch('/roster?role=coach').catch(() => [])
            ]);
            setAllMaintenanceTasks(tasks);
            setCoachList(Array.isArray(coaches) ? coaches : []);
            const task = tasks.find(t => t.id === taskId);
            if (task) {
              setEditingMaintenanceTask(task.id);
              setMaintenanceEditForm({
                status: task.status || 'submitted',
                repairNotes: task.repair_notes || '',
                assignedTo: task.assigned_to ? String(task.assigned_to) : ''
              });
            }
          } catch (e) {
            console.error("Failed to load maintenance task:", e);
            alert(e?.message || "Failed to load maintenance task");
          }
        }}
        onOyoWorkoutClick={(workoutId) => setOyoWorkoutModalId(workoutId)}
      />
    );
  }
  if (activeTab === "calendar") {
    return renderWithOyoModal(
      <PracticeCalendar squads={squads} coach={coach} userId={coach?.id || null} teamColors={teamColors} canEditPractices={canEditPractices} focusRequest={calendarFocusRequest} />
    );
  }
  if (activeTab === "lineups") {
    return renderWithOyoModal(
      <LineupsContent
        squads={squads}
        coach={coach}
        canEditLineups={coach?.permissions?.includes?.("lineups:edit") ?? false}
        focusRequest={lineupFocusRequest}
      />
    );
  }
  if (activeTab === "workouts") {
    if (!canViewWorkouts) {
      return renderWithOyoModal(
        <div className="card">
          <p className="help" style={{ margin: 0 }}>
            You do not have permission to view workouts.
          </p>
        </div>
      );
    }
    return renderWithOyoModal(
      <WorkoutTracker
        coach={coach}
        canEditWorkouts={canEditPractices}
        focusRequest={workoutFocusRequest}
      />
    );
  }
  if (activeTab === "maintenance") {
    return renderWithOyoModal(
      <div className="stack" style={{ gap: 12 }}>
        <EquipmentTasks user={coach} compactMode={false} focusRequest={maintenanceFocusRequest} />
      </div>
    );
  }
  if (activeTab === "coachattendance" || activeTab === "coachattendance-calendar") {
    return renderWithOyoModal(<CoachAttendance coach={coach} activeTab={activeTab} />);
  }

  if (activeTab === "attendance") {
    return renderWithOyoModal(
      <RosterAttendance
        roster={roster}
        rosterLoading={rosterLoading}
        rosterError={rosterError}
        coach={coach}
      />
    );
  }
  
if (activeTab === "roster") {
  return renderWithOyoModal(
    <div className="stack" style={{ gap: 12 }}>
      {rosterError && (
        <div className="card" style={{ padding: 12 }}>
          <p className="help" style={{ color: "red", margin: 0 }}>{rosterError}</p>
        </div>
      )}

      <RosterList
        roster={roster}
        loading={rosterLoading}
        onUpdated={handleRosterUpdated}
        canEditRoster={coach?.permissions?.includes?.("roster:edit") ?? false}
        coach={coach}
      />
    </div>
  );
}

  if (activeTab === "launchgrid") {
    return renderWithOyoModal(
      <LaunchAssignmentGrid coach={coach} />
    );
  }

  if (activeTab === "config") {
    if (!coach?.can_access_config && !coach?.can_manage_equipment) {
      return renderWithOyoModal(
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p>You do not have permission to access this feature.</p>
        </div>
      );
    }
    return renderWithOyoModal(<ConfigSettings user={coach} />);
  }
  if (activeTab === "profile") return renderWithOyoModal(<ProfileContent coach={coach} />);
  return renderWithOyoModal(null);
}
